export type GuideVideo = {
  provider: "YouTube" | "Vimeo";
  watchUrl: string;
  embedUrl: string | null;
};

const youtubeHosts = new Set(["youtube.com", "www.youtube.com", "youtu.be"]);
const vimeoHosts = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);
const youtubeId = /^[A-Za-z0-9_-]{11}$/;
const vimeoId = /^[1-9][0-9]{0,19}$/;
const vimeoHash = /^[A-Za-z0-9]{6,64}$/;

function startSeconds(value: string | null) {
  if (!value) return 0;
  if (/^\d{1,6}s?$/.test(value)) return Math.min(86400, Number.parseInt(value, 10));
  const match = value.match(/^(?:(\d{1,2})h)?(?:(\d{1,2})m)?(?:(\d{1,2})s)?$/);
  return match
    ? Math.min(
        86400,
        Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0),
      )
    : 0;
}

/** Resolve only the providers already accepted by Bible lesson URLs. No authored
 * query parameter, HTML or redirect URL is copied into an iframe destination.
 * Unrecognized provider paths retain their external link without an embed.
 * https://developers.google.com/youtube/player_parameters
 * https://developer.vimeo.com/api/oembed/videos
 */
export function guideVideo(value: string): GuideVideo | null {
  // oxlint-disable-next-line eslint/no-control-regex -- Reject control characters in authored video URLs.
  if (value.length > 1000 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
  const source = value.trim();
  if (!/^https:\/\//i.test(source)) return null;
  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
  const youtube = youtubeHosts.has(url.hostname);
  if (!youtube && !vimeoHosts.has(url.hostname)) return null;
  const result: GuideVideo = {
    provider: youtube ? "YouTube" : "Vimeo",
    watchUrl: url.href,
    embedUrl: null,
  };
  const path = url.pathname.replace(/\/$/, "");
  if (youtube) {
    const id =
      url.hostname === "youtu.be"
        ? path.match(/^\/([^/]+)$/)?.[1]
        : path === "/watch"
          ? url.searchParams.getAll("v").length === 1
            ? url.searchParams.get("v")
            : null
          : path.match(/^\/(?:embed|shorts|live)\/([^/]+)$/)?.[1];
    if (!id || !youtubeId.test(id)) return result;
    const embed = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
    embed.searchParams.set("autoplay", "1");
    embed.searchParams.set("playsinline", "1");
    const start = startSeconds(url.searchParams.get("start") ?? url.searchParams.get("t"));
    if (start) embed.searchParams.set("start", String(start));
    return { ...result, embedUrl: embed.href };
  }
  const match =
    url.hostname === "player.vimeo.com"
      ? path.match(/^\/video\/([0-9]+)$/)
      : path.match(/^\/([0-9]+)(?:\/([A-Za-z0-9]+))?$/);
  const id = match?.[1];
  if (!id || !vimeoId.test(id)) return result;
  const pathHash = match?.[2];
  const queryHashes = url.searchParams.getAll("h");
  const hash = pathHash ?? queryHashes[0];
  if (
    queryHashes.length > 1 ||
    (hash && !vimeoHash.test(hash)) ||
    (pathHash && queryHashes.length && pathHash !== queryHashes[0])
  )
    return result;
  const embed = new URL(`https://player.vimeo.com/video/${id}`);
  if (hash) embed.searchParams.set("h", hash);
  embed.searchParams.set("autoplay", "1");
  embed.searchParams.set("dnt", "1");
  const start = startSeconds(new URLSearchParams(url.hash.slice(1)).get("t"));
  if (start) embed.hash = `t=${start}s`;
  return { ...result, embedUrl: embed.href };
}
